export interface OrderSet {
  id: string;
  updatedAt: string | null;
  transactions: OrderTransaction[];
}

export interface OrderTransaction {
  key: string;
  side: TransactionSide;
  assetId: string | null;
  quantity: number | null;
  amount: number | null;
  amountSek: number | null;
  sellAll: boolean;
  pricedAt: string | null;
}

export type TransactionSide = "BUY" | "SELL";

export interface OrderAsset {
  name: string | null;
  isin: string | null;
  ticker: string | null;
  vehicleType: string | null;
}

export function orderSetOf(json: unknown, id: string): OrderSet {
  const raw = isObject(json) ? json : {};
  const listed = Array.isArray(raw.transactions) ? raw.transactions : [];
  const updatedAt = text(raw.updated_at);
  return {
    id,
    updatedAt,
    transactions: listed.filter(isObject).flatMap((row, index): OrderTransaction[] => {
      const side = String(row.transaction_type ?? "").toUpperCase();
      if (side !== "BUY" && side !== "SELL") return [];
      const assetId = text(row.quant_asset_id);
      return [
        {
          key: `${side}:${assetId ?? "?"}:${index}`,
          side,
          assetId,
          quantity: number(row.quantity),
          amount: number(row.amount),
          amountSek: number(row.amount_sek),
          sellAll: row.sell_all === true,
          pricedAt: text(row.quantified_at),
        },
      ];
    }),
  };
}

export function transactionsOn(set: OrderSet, side: TransactionSide): OrderTransaction[] {
  return set.transactions
    .filter((transaction) => transaction.side === side)
    .sort((a, b) => (b.amountSek ?? 0) - (a.amountSek ?? 0));
}

export function sideTotalSek(transactions: readonly OrderTransaction[]): number | null {
  if (transactions.some((transaction) => transaction.amountSek === null)) return null;
  return transactions.reduce((sum, transaction) => sum + (transaction.amountSek ?? 0), 0);
}

export function priceSekOf(transaction: OrderTransaction): number | null {
  const { amountSek, quantity } = transaction;
  if (amountSek === null || quantity === null || quantity === 0) return null;
  return Math.abs(amountSek / quantity);
}

export function setQuantifiedAt(set: OrderSet): string | null {
  let oldest: string | null = null;
  for (const transaction of set.transactions) {
    const at = transaction.pricedAt;
    if (at !== null && (oldest === null || instant(at) < instant(oldest))) oldest = at;
  }
  return oldest ?? set.updatedAt;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function text(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function number(value: unknown): number | null {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : null;
}

function instant(value: string): number {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
